<template>
  <el-drawer
    :visible.sync="visibleProxy"
    title="线索详情"
    size="64%"
    :with-header="true"
    @close="handleClose"
  >
    <div v-if="detail" class="lead-detail-drawer">
      <section class="detail-hero detail-section">
        <div class="detail-hero__content">
          <div class="detail-hero__eyebrow">请认真审核！</div>
          <div class="detail-hero__title-row">
            <h3 class="detail-hero__title">{{ leadUnitName }}</h3>
            <el-tag size="medium" effect="dark" :type="statusTagType">{{ statusLabel }}</el-tag>
          </div>
          <p class="detail-hero__desc">
            快速核对线索信息、补全学员资料，并在同一侧边栏内查看完整会话内容。
          </p>
        </div>
        <div class="detail-hero__stats">
          <div class="detail-stat">
            <span class="detail-stat__label">搜索词</span>
            <span class="detail-stat__value">{{ leadSearchWord }}</span>
          </div>
          <div class="detail-stat">
            <span class="detail-stat__label">关键词</span>
            <span class="detail-stat__value">{{ leadKeyword }}</span>
          </div>
        </div>
      </section>

      <el-card shadow="never" class="detail-section detail-card">
        <div slot="header" class="detail-card__header">
          <span>审核处理</span>
          <span class="detail-card__hint">字段与新增学员保持一致</span>
        </div>
        <el-form ref="form" :model="form" :rules="rules" label-width="100px" class="review-form">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="来源渠道" prop="sourceCode">
                <el-select v-model="form.sourceCode" placeholder="请选择来源渠道" filterable style="width: 100%;">
                  <el-option v-for="item in sourceOptions" :key="item.code" :label="item.name" :value="item.code" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="学员姓名" prop="studentName">
                <div class="student-name-row">
                  <el-input v-model.trim="form.studentName" placeholder="请输入学员姓名" clearable />
                  <el-button plain class="view-conversation-btn" @click="$emit('open-conversation')">查看会话</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="手机号" prop="phoneNumber">
                <div class="phone-input-row">
                  <el-input
                    v-model.trim="form.phoneNumber"
                    placeholder="请输入手机号"
                    clearable
                    @blur="handlePhoneBlur"
                    @change="handlePhoneChange"
                  />
                  <span v-if="phoneAttributionText" class="phone-attribution-text">{{ phoneAttributionText }}</span>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="微信号" prop="wechatNumber">
                <el-input v-model.trim="form.wechatNumber" placeholder="选填" maxlength="20" clearable />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="咨询课目" prop="projectCodes" class="project-form-item">
            <el-cascader
              v-model="form.projectCodes"
              :options="projectOptions"
              :props="{ multiple: true, checkStrictly: true, emitPath: false, expandTrigger: 'hover' }"
              placeholder="请选择咨询课目"
              clearable
              filterable
              collapse-tags
              style="width: 100%;"
            />
          </el-form-item>

          <el-form-item label="所在城市" prop="cityCode">
            <region-cascader
              ref="regionCascader"
              v-model="form.cityCode"
              placeholder="请选择省市区"
              width="100%"
              check-strictly
            />
          </el-form-item>

          <el-form-item label="详细地址" prop="addressDetail">
            <div class="address-input-row">
              <el-input
                v-model.trim="form.addressDetail"
                type="textarea"
                :rows="2"
                placeholder="选填，点击右侧按钮可地图选点或搜索地址"
                maxlength="200"
                show-word-limit
              />
              <el-button icon="el-icon-location-information" @click="openAddressMap" style="height: stretch;" type="primary">地图</el-button>
            </div>
          </el-form-item>

          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="3"
              placeholder="请输入备注（最多500字）"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>

          <el-form-item label="审核备注" prop="reviewRemark">
            <el-input v-model="form.reviewRemark" type="textarea" :rows="3" placeholder="请输入审核备注" />
          </el-form-item>
          <el-form-item label="无效原因">
            <el-select v-model="form.invalidReasonCode" placeholder="拒绝时请选择" clearable style="width: 100%;">
              <el-option label="恶意/骚扰" value="MALICIOUS" />
              <el-option label="无效对话" value="INVALID_DIALOG" />
              <el-option label="空错号" value="INVALID_PHONE" />
              <el-option label="三次未接通" value="INVALID_UNREACHABLE" />
              <el-option label="非本人" value="INVALID_NOT_SELF" />
              <el-option label="无意向" value="INVALID_NO_INTENT" />
            </el-select>
          </el-form-item>
          <el-form-item label="关联学员ID">
            <el-input v-model.trim="form.studentId" placeholder="如需关联已有学员，请输入 studentId" />
          </el-form-item>
          <el-form-item class="review-form__actions">
            <el-button type="success" @click="handleApprove">审核通过</el-button>
            <el-button type="danger" plain @click="handleReject">审核拒绝</el-button>
            <el-button @click="handleLink">关联已有学员</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card shadow="never" class="detail-section detail-card detail-card--raw">
        <el-collapse v-model="rawRequestActiveNames" class="raw-request-collapse">
          <el-collapse-item name="raw-request">
            <template slot="title">
              <div class="detail-card__header raw-request-collapse__title">
                <span>原始请求（脱敏）</span>
                <span class="detail-card__hint">用于排查字段映射或来源数据问题</span>
              </div>
            </template>
            <pre class="json-preview">{{ previewJson(detail.lead.rawPayloadJson) }}</pre>
          </el-collapse-item>
        </el-collapse>
      </el-card>
    </div>

    <el-drawer
      :visible.sync="conversationVisibleProxy"
      title="会话详情"
      size="42%"
      append-to-body
      custom-class="lead-detail-conversation-drawer"
      @close="$emit('close-conversation')"
    >
      <div class="conversation-drawer-body">
        <baidu-conversation-panel
          :loading="conversationLoading"
          :conversation="conversation"
          embedded
        />
      </div>
    </el-drawer>

    <el-dialog
      title="选择详细地址"
      :visible.sync="addressMapVisible"
      width="980px"
      append-to-body
      :close-on-click-modal="false"
      @opened="initAddressMap"
      @close="destroyAddressMap"
    >
      <div class="address-map-dialog">
        <div class="address-map-toolbar">
          <el-input
            v-model.trim="addressSearchKeyword"
            placeholder="输入地址、商圈或校区名称搜索"
            clearable
            @keyup.enter.native="searchAddress"
          />
          <el-button type="primary" icon="el-icon-search" :loading="addressSearchLoading" @click="searchAddress">搜索</el-button>
        </div>
        <div v-if="addressSearchResults.length" class="address-result-list">
          <button
            v-for="item in addressSearchResults"
            :key="`${item.longitude}-${item.latitude}-${item.displayName}`"
            type="button"
            class="address-result-item"
            @click="selectAddressResult(item)"
          >
            <span>{{ item.displayName }}</span>
            <small>{{ item.longitude }}, {{ item.latitude }}</small>
          </button>
        </div>
        <div ref="addressMapEl" class="address-map-canvas"></div>
        <div class="address-map-footer">
          <span class="address-map-tip">{{ addressMapTip }}</span>
          <div class="address-map-actions">
            <el-button @click="addressMapVisible = false">取消</el-button>
            <el-button type="primary" :disabled="!pickedAddressText" @click="confirmPickedAddress">确认地址</el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </el-drawer>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import api from '@/services/api';
import RegionCascader from '@/components/RegionCascader.vue';
import {
  getRegionPathFromRows,
  isFuzzyMatch,
  isMobilePhone,
  isWechatId,
  matchRegionCode
} from '@/utils/baiduLeadReviewPrefill.mjs';
import BaiduConversationPanel from './BaiduConversationPanel.vue';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

const DEFAULT_SOURCE_CODE = 'BAIDU_PROMO';
const TDT_TOKEN = process.env.VUE_APP_TDT_TOKEN || '';
const TDT_SK = process.env.VUE_APP_TDT_SK || '';
const TDT_TILE_QUERY = () => {
  let q = 'tk=' + TDT_TOKEN;
  if (TDT_SK) q += '&sk=' + TDT_SK;
  return q;
};

function splitTags(input) {
  return String(input || '')
    .split(/[、,，;；\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildProjectCascader(items) {
  const parents = items.filter(item => !item.parent_code && !item.parentCode);
  return parents.map(parent => ({
    value: parent.code,
    label: parent.name,
    children: items
      .filter(child => (child.parent_code || child.parentCode) === parent.code)
      .map(child => ({
        value: child.code,
        label: child.name
      }))
  }));
}

export default {
  name: 'BaiduLeadDetailDrawer',
  components: {
    BaiduConversationPanel,
    RegionCascader
  },
  props: {
    visible: { type: Boolean, default: false },
    detail: { type: Object, default: null },
    conversationVisible: { type: Boolean, default: false },
    conversationLoading: { type: Boolean, default: false },
    conversation: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const validatePhone = (rule, value, callback) => {
      if (!String(value || '').trim()) return callback(new Error('请输入手机号'));
      if (!isMobilePhone(value)) return callback(new Error('请输入正确的手机号'));
      return callback();
    };
    const validateWechat = (rule, value, callback) => {
      if (!String(value || '').trim()) return callback();
      if (!isWechatId(value)) return callback(new Error('请输入正确的微信号'));
      return callback();
    };
    return {
      form: this.createEmptyForm(),
      sourceOptions: [],
      projectOptions: [],
      projectRows: [],
      regionRows: [],
      dictionariesLoaded: false,
      phoneAttributionText: '',
      phoneAttributionSeq: 0,
      rules: {
        sourceCode: [{ required: true, message: '请选择来源渠道', trigger: ['blur', 'change'] }],
        studentName: [{ required: true, message: '请输入学员姓名', trigger: ['blur', 'change'] }],
        phoneNumber: [
          { required: true, message: '请输入手机号', trigger: ['blur', 'change'] },
          { validator: validatePhone, trigger: ['blur', 'change'] }
        ],
        wechatNumber: [{ validator: validateWechat, trigger: ['blur', 'change'] }],
        projectCodes: [{ required: true, type: 'array', min: 1, message: '请选择咨询课目', trigger: ['blur', 'change'] }],
        cityCode: [{ required: true, message: '请选择所在城市', trigger: ['blur', 'change'] }],
        remark: [{ max: 500, message: '备注最多500字', trigger: ['blur', 'change'] }],
        addressDetail: [{ max: 200, message: '详细地址最多200字', trigger: ['blur', 'change'] }]
      },
      rawRequestActiveNames: [],
      addressMapVisible: false,
      addressMap: null,
      addressMarker: null,
      addressSearchKeyword: '',
      addressSearchLoading: false,
      addressSearchResults: [],
      pickedAddressText: '',
      pickedPoint: null,
      chinaBounds: [
        [0.8293, 72.004],
        [55.8271, 137.8347]
      ]
    };
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    conversationVisibleProxy: {
      get() {
        return this.conversationVisible;
      },
      set(value) {
        this.$emit('update:conversation-visible', value);
      }
    },
    reviewStatus() {
      return this.detail && this.detail.review ? this.detail.review.reviewStatus : 'WAITING';
    },
    statusLabel() {
      return this.formatStatus(this.reviewStatus);
    },
    statusTagType() {
      return {
        WAITING: 'warning',
        APPROVED: 'success',
        REJECTED_INVALID: 'danger',
        REJECTED_DUPLICATED: 'info',
        REJECTED_INCOMPLETE: 'info'
      }[this.reviewStatus] || '';
    },
    leadStandardFields() {
      return this.detail && this.detail.lead && this.detail.lead.standardFields
        ? this.detail.lead.standardFields
        : {};
    },
    leadUnitName() {
      return this.leadStandardFields.unitName || '-';
    },
    leadSearchWord() {
      return this.leadStandardFields.searchWord || '-';
    },
    leadKeyword() {
      return this.leadStandardFields.keyword || '-';
    },
    addressMapTip() {
      if (this.pickedAddressText) return `已选择：${this.pickedAddressText}`;
      return '可搜索地址后选择结果，也可在地图上点击一个点位自动反查地址。';
    }
  },
  watch: {
    visible(value) {
      if (value) this.loadDictionariesAndFill();
    },
    detail: {
      immediate: true,
      handler() {
        if (this.visible) this.loadDictionariesAndFill();
        else this.fillFormFromDetail();
      }
    }
  },
  created() {
    this.loadDictionaries();
  },
  methods: {
    createEmptyForm() {
      return {
        sourceCode: '',
        studentName: '',
        phoneNumber: '',
        wechatNumber: '',
        projectCodes: [],
        cityCode: '',
        addressDetail: '',
        remark: '',
        reviewRemark: '',
        invalidReasonCode: '',
        studentId: ''
      };
    },
    async loadDictionariesAndFill() {
      await this.loadDictionaries();
      this.fillFormFromDetail();
    },
    async loadDictionaries() {
      if (this.dictionariesLoaded) return;
      try {
        const res = await api.post('/dictionary', { tags: ['STUDENT_SOURCE', 'MAIN_PROJECT', 'REGION'] });
        if (res.data && res.data.success) {
          const dict = res.data.data || {};
          this.sourceOptions = this.removeDuplicates(dict.STUDENT_SOURCE || []);
          this.projectRows = this.removeDuplicates(dict.MAIN_PROJECT || []);
          this.regionRows = this.removeDuplicates(dict.REGION || []);
          this.projectOptions = buildProjectCascader(this.projectRows);
          this.dictionariesLoaded = true;
        }
      } catch (e) {
        this.$message.error('加载来源、课目或地区字典失败');
      }
    },
    removeDuplicates(rows) {
      const seen = new Set();
      return (Array.isArray(rows) ? rows : []).filter(item => {
        const key = `${item.tag || ''}:${item.code || ''}`;
        if (!item || !item.code || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    getDefaultSourceCode(payloadSourceCode) {
      const sourceCode = String(payloadSourceCode || '').trim();
      if (sourceCode && this.sourceOptions.some(item => item.code === sourceCode)) return sourceCode;
      const baiduPromo = this.sourceOptions.find(item => item.code === DEFAULT_SOURCE_CODE || item.name === '百度推广');
      return baiduPromo ? baiduPromo.code : (this.sourceOptions[0] ? this.sourceOptions[0].code : DEFAULT_SOURCE_CODE);
    },
    fillFormFromDetail() {
      this.rawRequestActiveNames = [];
      this.phoneAttributionText = '';
      const value = this.detail || {};
      const payload = value.review && value.review.studentPayloadJson ? value.review.studentPayloadJson : {};
      const lead = value.lead || {};
      const projectCodes = Array.isArray(payload.projectCodes) && payload.projectCodes.length
        ? payload.projectCodes
        : this.matchProjectCodes(lead.projectCandidate);
      const cityCode = payload.cityCode || matchRegionCode(lead.standardFields || {}, this.regionRows);
      const remark = payload.remark || this.buildRemarkFromSummary(value.conversationSummary);

      this.form = {
        sourceCode: this.getDefaultSourceCode(payload.sourceCode),
        studentName: payload.studentName || lead.nameCandidate || '',
        phoneNumber: payload.phoneNumber || lead.phoneCandidate || '',
        wechatNumber: Array.isArray(payload.wechatNumbers) && payload.wechatNumbers[0] ? payload.wechatNumbers[0] : '',
        projectCodes,
        cityCode,
        addressDetail: payload.addressDetail || '',
        remark,
        reviewRemark: value.review ? value.review.reviewRemark || '' : '',
        invalidReasonCode: value.review ? value.review.invalidReasonCode || '' : '',
        studentId: value.studentLink ? value.studentLink.studentId || '' : ''
      };
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
        if (this.$refs.regionCascader && this.form.cityCode) this.$refs.regionCascader.setValue(this.form.cityCode);
      });
    },
    buildRemarkFromSummary(summary) {
      return splitTags(summary).join('、');
    },
    matchProjectCodes(projectCandidate) {
      const text = String(projectCandidate || '').trim();
      if (!text || !this.projectRows.length) return [];
      const tags = splitTags(text);
      const candidates = tags.length ? tags : [text];
      const result = [];
      candidates.forEach(raw => {
        const match = this.projectRows.find(row =>
          isFuzzyMatch(raw, row.name) ||
          isFuzzyMatch(raw, row.code) ||
          isFuzzyMatch(raw, row.remark)
        );
        if (match && !result.includes(match.code)) result.push(match.code);
      });
      return result;
    },
    getRegionPath() {
      const path = this.$refs.regionCascader && this.$refs.regionCascader.getFullPath
        ? this.$refs.regionCascader.getFullPath(this.form.cityCode)
        : [];
      if (path.length) return path;
      return getRegionPathFromRows(this.form.cityCode, this.regionRows);
    },
    handlePhoneChange() {
      this.refreshPhoneAttribution();
    },
    handlePhoneBlur() {
      this.refreshPhoneAttribution();
    },
    applyPhoneAttributionRegion(region) {
      if (!region || !region.provinceCode || !region.cityCode) return;
      this.form.cityCode = region.cityCode;
      this.$nextTick(() => {
        if (this.$refs.regionCascader && this.form.cityCode) this.$refs.regionCascader.setValue(this.form.cityCode);
        this.$refs.form && this.$refs.form.validateField('cityCode');
      });
    },
    async refreshPhoneAttribution() {
      const phone = String(this.form.phoneNumber || '').trim().replace(/\s+/g, '');
      const seq = ++this.phoneAttributionSeq;
      if (!isMobilePhone(phone)) {
        this.phoneAttributionText = '';
        return;
      }
      try {
        const res = await api.post('/student/phone-attribution', { phoneNumber: phone });
        if (seq !== this.phoneAttributionSeq) return;
        const data = res && res.data && res.data.data ? res.data.data : {};
        this.phoneAttributionText = data.displayText || '';
        if (data.matched && data.region) this.applyPhoneAttributionRegion(data.region);
      } catch (e) {
        if (seq === this.phoneAttributionSeq) this.phoneAttributionText = '';
      }
    },
    buildPayload() {
      const phone = String(this.form.phoneNumber || '').trim().replace(/\s+/g, '');
      const wechat = String(this.form.wechatNumber || '').trim();
      const regionPath = this.getRegionPath();
      return {
        sourceCode: this.form.sourceCode,
        studentName: this.form.studentName,
        phoneNumber: phone,
        phoneNumbers: phone ? [phone] : [],
        wechatNumbers: wechat ? [wechat] : [],
        projectCodes: this.form.projectCodes || [],
        cityCode: this.form.cityCode || '',
        provinceCode: regionPath[0] ? regionPath[0].code : '',
        cityCodeRegion: regionPath[1] ? regionPath[1].code : '',
        districtCode: regionPath[2] ? regionPath[2].code : '',
        addressDetail: this.form.addressDetail || '',
        remark: this.form.remark || '',
        validStatusCode: 'PENDING',
        reviewRemark: this.form.reviewRemark
      };
    },
    previewJson(value) {
      return JSON.stringify(value || {}, null, 2);
    },
    formatStatus(status) {
      return {
        WAITING: '待审核',
        APPROVED: '已通过',
        REJECTED_INVALID: '无效拒绝',
        REJECTED_DUPLICATED: '重复线索',
        REJECTED_INCOMPLETE: '信息不完整'
      }[status] || status;
    },
    async handleApprove() {
      try {
        await this.$refs.form.validate();
      } catch (e) {
        this.$nextTick(() => {
          const errorEl = document.querySelector('.lead-detail-drawer .el-form-item.is-error');
          if (errorEl) errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      this.$emit('approve', this.buildPayload());
    },
    handleReject() {
      this.$emit('reject', {
        invalidReasonCode: this.form.invalidReasonCode,
        reviewRemark: this.form.reviewRemark
      });
    },
    handleLink() {
      this.$emit('link-student', {
        studentId: this.form.studentId,
        reviewRemark: this.form.reviewRemark
      });
    },
    handleClose() {
      this.destroyAddressMap();
      this.$emit('close');
    },
    openAddressMap() {
      this.addressSearchKeyword = this.form.addressDetail || '';
      this.addressSearchResults = [];
      this.pickedAddressText = this.form.addressDetail || '';
      this.pickedPoint = null;
      this.addressMapVisible = true;
    },
    initAddressMap() {
      if (this.addressMap || !this.$refs.addressMapEl) {
        if (this.addressMap) this.addressMap.invalidateSize();
        return;
      }
      this.addressMap = L.map(this.$refs.addressMapEl, {
        zoomControl: true,
        minZoom: 4,
        maxBounds: this.chinaBounds,
        maxBoundsViscosity: 1.0,
        worldCopyJump: false
      }).setView([35.8617, 104.1954], 4);

      const tdtVecUrl =
        'https://t{s}.tianditu.gov.cn/vec_w/wmts?' +
        'SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&' +
        'LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&' +
        'TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&' + TDT_TILE_QUERY();
      const tdtCvaUrl =
        'https://t{s}.tianditu.gov.cn/cva_w/wmts?' +
        'SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&' +
        'LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&' +
        'TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&' + TDT_TILE_QUERY();

      L.tileLayer(tdtVecUrl, {
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        minZoom: 4,
        maxZoom: 18,
        noWrap: true,
        bounds: this.chinaBounds,
        crossOrigin: true,
        attribution: '&copy; 天地图'
      }).addTo(this.addressMap);
      L.tileLayer(tdtCvaUrl, {
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        minZoom: 4,
        maxZoom: 18,
        noWrap: true,
        bounds: this.chinaBounds,
        crossOrigin: true
      }).addTo(this.addressMap);
      this.addressMap.on('click', this.handleAddressMapClick);
      this.$nextTick(() => this.addressMap && this.addressMap.invalidateSize());
    },
    destroyAddressMap() {
      if (this.addressMap) {
        this.addressMap.off('click', this.handleAddressMapClick);
        this.addressMap.remove();
        this.addressMap = null;
      }
      this.addressMarker = null;
    },
    placeAddressMarker(lat, lng) {
      if (!this.addressMap) return;
      if (this.addressMarker) this.addressMap.removeLayer(this.addressMarker);
      this.addressMarker = L.marker([lat, lng], { draggable: true }).addTo(this.addressMap);
      this.addressMarker.on('dragend', evt => {
        const pos = evt.target.getLatLng();
        this.reverseAddress(pos.lng, pos.lat);
      });
      this.addressMap.setView([lat, lng], 16);
    },
    async searchAddress() {
      const text = String(this.addressSearchKeyword || '').trim();
      if (!text) {
        this.$message.warning('请输入地址关键词');
        return;
      }
      this.addressSearchLoading = true;
      try {
        const res = await api.post('/map/geocode', { text }, { timeout: 300000 });
        if (!(res.data && res.data.success && res.data.data)) {
          this.$message.error((res.data && res.data.message) || '地址搜索失败');
          return;
        }
        const item = res.data.data;
        this.addressSearchResults = [item];
        this.selectAddressResult(item);
      } catch (e) {
        this.$message.error('地址搜索失败，请输入更完整地址');
      } finally {
        this.addressSearchLoading = false;
      }
    },
    selectAddressResult(item) {
      const lng = Number(item.longitude);
      const lat = Number(item.latitude);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return;
      this.pickedAddressText = item.displayName || this.addressSearchKeyword || '';
      this.pickedPoint = { lng, lat };
      this.placeAddressMarker(lat, lng);
    },
    async handleAddressMapClick(e) {
      if (!e || !e.latlng) return;
      const { lat, lng } = e.latlng;
      this.placeAddressMarker(lat, lng);
      await this.reverseAddress(lng, lat);
    },
    async reverseAddress(lng, lat) {
      this.pickedPoint = { lng: Number(lng), lat: Number(lat) };
      try {
        const res = await api.post('/map/reverse-geocode', { longitude: lng, latitude: lat }, { timeout: 300000 });
        if (res.data && res.data.success && res.data.data) {
          this.pickedAddressText = res.data.data.displayName || `${Number(lng).toFixed(6)},${Number(lat).toFixed(6)}`;
          return;
        }
        this.pickedAddressText = `${Number(lng).toFixed(6)},${Number(lat).toFixed(6)}`;
      } catch (e) {
        this.pickedAddressText = `${Number(lng).toFixed(6)},${Number(lat).toFixed(6)}`;
      }
    },
    confirmPickedAddress() {
      if (!this.pickedAddressText) return;
      this.form.addressDetail = this.pickedAddressText;
      this.addressMapVisible = false;
    }
  }
};
</script>

<style scoped>
.lead-detail-drawer {
  padding: 4px 16px 24px;
}

.detail-section {
  margin-bottom: 18px;
}

.detail-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
  border-radius: 8px;
  background: #f6fbf4;
  border: 1px solid #d9ead3;
}

.detail-hero__content {
  flex: 1;
  min-width: 0;
}

.detail-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(103, 168, 96, 0.12);
  color: #4e7d46;
  font-size: 12px;
  font-weight: 700;
}

.detail-hero__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.detail-hero__title {
  margin: 0;
  color: #1f2d1a;
  font-size: 24px;
  line-height: 1.25;
  font-weight: 700;
  word-break: break-word;
}

.detail-hero__desc {
  margin: 12px 0 0;
  max-width: 56ch;
  color: #5c6d56;
  font-size: 14px;
  line-height: 1.75;
}

.detail-hero__stats {
  min-width: 240px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.detail-stat {
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(103, 168, 96, 0.18);
}

.detail-stat__label {
  display: block;
  color: #7a8a75;
  font-size: 12px;
}

.detail-stat__value {
  display: block;
  margin-top: 8px;
  color: #23331f;
  font-size: 16px;
  font-weight: 700;
  word-break: break-word;
}

.detail-card {
  border-radius: 8px;
  border: 1px solid #e7efe3;
}

.detail-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #22311f;
  font-weight: 700;
}

.detail-card__hint {
  color: #7d8b77;
  font-size: 12px;
  font-weight: 500;
}

.review-form :deep(.el-form-item) {
  margin-bottom: 20px;
}

.student-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.student-name-row .el-input {
  flex: 1;
}

.phone-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.phone-input-row .el-input {
  flex: 1;
}

.phone-attribution-text {
  flex-shrink: 0;
  color: #409eff;
  font-size: 13px;
  white-space: nowrap;
}

.view-conversation-btn {
  flex-shrink: 0;
  color: #507b48;
  border-color: rgba(80, 123, 72, 0.24);
  background: #f7fbf5;
}

.project-form-item :deep(.el-cascader__tags) {
  max-width: calc(100% - 36px);
}

.address-input-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.address-input-row .el-textarea {
  flex: 1;
}

.address-input-row .el-button {
  flex-shrink: 0;
}

.review-form__actions :deep(.el-form-item__content) {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.json-preview {
  margin: 0;
  max-height: 320px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  padding: 14px 16px;
  border-radius: 8px;
}

.detail-card--raw :deep(.el-card__body) {
  padding: 0 20px;
}

.raw-request-collapse {
  border: 0;
}

.raw-request-collapse :deep(.el-collapse-item__header) {
  border-bottom: 0;
  height: 54px;
}

.raw-request-collapse :deep(.el-collapse-item__wrap) {
  border-bottom: 0;
}

.raw-request-collapse :deep(.el-collapse-item__content) {
  padding-bottom: 20px;
}

.raw-request-collapse__title {
  flex: 1;
  padding-right: 12px;
}

.conversation-drawer-body {
  padding: 12px 16px 20px;
}

.address-map-dialog {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-map-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.address-map-toolbar .el-input {
  flex: 1;
}

.address-result-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 110px;
  overflow: auto;
}

.address-result-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  color: #303133;
  cursor: pointer;
  text-align: left;
}

.address-result-item:hover {
  border-color: #409eff;
  color: #409eff;
}

.address-result-item small {
  color: #909399;
  white-space: nowrap;
}

.address-map-canvas {
  width: 100%;
  height: 460px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
}

.address-map-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.address-map-tip {
  flex: 1;
  min-width: 0;
  color: #606266;
  font-size: 13px;
  word-break: break-word;
}

.address-map-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 1280px) {
  .detail-hero {
    flex-direction: column;
  }

  .detail-hero__stats {
    min-width: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .student-name-row,
  .address-map-toolbar,
  .address-map-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .detail-hero__stats {
    grid-template-columns: 1fr;
  }

  .address-map-canvas {
    height: 360px;
  }
}
</style>
